import { useState, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import ConfirmChartRegister from 'pages/modal/ConfirmChartRegister';
import PlaqueControlRecordChart from '../PlaqueControlRecordChart';
import {
  PlaqueControlRecordChartFields,
  PlaqueControlRecordChartFormDefaultValues,
} from 'constants/chart/plaqueControlRecordChart';
import { TPlaqueControlRecordChartFormValues } from 'types/chart/plaqueControlRecordChart';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';
import {
  TEvaluationChartInfo,
  TPutEvaluationChartResponse,
} from 'types/apis/chart/studentEvaluationChart';
import useModal from 'hooks/useModal';
import { TChartInfoResponse } from 'hooks/chart/useGetChartInfo';
import useChartRegister from 'hooks/chart/useChartRegister';

type TProps = {
  query: URLSearchParams;
  chartType: number;
  category: string;
  id: number;
  isRegister: boolean;
  setSubmitEndDate: (submitEndDate: string) => void;
  chartInfoResponse: TChartInfoResponse | null;
  setChartInfoResponse: (data: any) => void;
  activeTabIndex: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isEdit: boolean;
  isReadOnly: boolean;
  checkSubmitEndDate: (submitEndDate: string) => void;
  handleSuccessGetChartInfo: (data: TChartInfoResponse) => void;
  setCanvas1Ref: (ref: any) => void;
  setCanvas2Ref: (ref: any) => void;
};

const PlaqueControlRecordChartRegister = ({
  query,
  chartType,
  category,
  id,
  isRegister,
  setSubmitEndDate,
  chartInfoResponse,
  setChartInfoResponse,
  activeTabIndex,
  isLoading,
  setIsLoading,
  isEdit,
  isReadOnly,
  checkSubmitEndDate,
  handleSuccessGetChartInfo,
  setCanvas1Ref,
  setCanvas2Ref,
}: TProps) => {
  const { openModal } = useModal();

  const [round1All, setRound1All] = useState(false);
  const [round2All, setRound2All] = useState(false);

  const { chartRegister } = useChartRegister(({ data }) => afterAutoSave(data));

  const methods = useForm<TPlaqueControlRecordChartFormValues>({
    defaultValues: PlaqueControlRecordChartFormDefaultValues,
  });
  const { handleSubmit, setValue, watch, reset } = methods;
  const formValues = watch();
  const formValuesRef = useRef(formValues);
  const prevFormValuesRef = useRef<TPlaqueControlRecordChartFormValues | null>(
    null
  );

  // 실습용 - 자동 저장 (20초 간격)
  useEffect(() => {
    if (isLoading) return;
    prevFormValuesRef.current = formValues;
  }, [isLoading]);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (!isLoading && isRegister && category === 'evaluation') {
      intervalId = setInterval(() => {
        if (isReadOnly) return;
        // 초기 상태 자동 저장 방지
        const chartInfoList = chartInfoResponse?.chartInfo as
          | TEvaluationChartInfo[]
          | undefined;
        if (
          chartInfoList &&
          chartInfoList.length > (activeTabIndex > 1 ? 1 : 0)
        ) {
          const currentChartInfo = chartInfoList[activeTabIndex > 1 ? 1 : 0];
          if (
            currentChartInfo?.chartData === null &&
            JSON.stringify(PlaqueControlRecordChartFormDefaultValues) ===
              JSON.stringify(formValuesRef.current)
          ) {
            return;
          }
        }

        // 이전 값과 비교하여 변경된 경우만 자동 저장 요청
        if (
          JSON.stringify(formValuesRef.current) !==
          JSON.stringify(prevFormValuesRef.current)
        ) {
          onValid(true, formValuesRef.current);
        }
      }, 20000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRegister, isLoading, category, isReadOnly]);

  const afterAutoSave = ({ chartInfo }: TPutEvaluationChartResponse) => {
    setChartInfoResponse((prev: any) => {
      if (!prev) return prev;
      const newChartInfo = prev.chartInfo as TEvaluationChartInfo[];
      const newChartInfoData = [...newChartInfo];
      newChartInfoData[activeTabIndex > 1 ? 1 : 0] = {
        ...newChartInfoData[activeTabIndex > 1 ? 1 : 0],
        chartData: chartInfo?.chartData || null,
        fileInfoList: chartInfo?.fileInfoList || [],
      };
      return {
        ...prev,
        chartInfo: newChartInfoData,
      };
    });
    prevFormValuesRef.current = chartInfo?.chartData || null;
  };

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    let chartData: TPlaqueControlRecordChartFormValues | null =
      (chartInfo?.chartData as TPlaqueControlRecordChartFormValues | null) ||
      null;

    if (category !== 'practice') {
      const submitEndDate =
        (chartInfo as TEvaluationChartInfo)?.submitEndDate || '';
      setSubmitEndDate(submitEndDate);
      checkSubmitEndDate(submitEndDate);

      const prevData =
        (chartInfoResponse?.chartInfo as TEvaluationChartInfo[])[0]
          ?.chartData || null;
      if (prevData && activeTabIndex > 1) {
        const filteredData = Object.keys(prevData)
          .filter(
            (key) =>
              key.includes('1') ||
              key.includes('next') ||
              key.includes('Method')
          )
          .reduce(
            (obj, key) => {
              (obj as Record<string, any>)[key] =
                prevData[key as keyof typeof prevData];
              return obj;
            },
            {} as Partial<typeof prevData>
          );
        if (!chartData) {
          chartData =
            PlaqueControlRecordChartFormDefaultValues as TPlaqueControlRecordChartFormValues;
        }
        Object.keys(filteredData).forEach((key) => {
          if (!chartData) return;
          chartData[key as keyof TPlaqueControlRecordChartFormValues] =
            filteredData[key as keyof typeof filteredData];
        });
      }
    }
    if (!chartData) {
      reset();
      setIsLoading(false);
      return;
    }
    Object.keys(chartData).forEach((key) => {
      if (!chartData) return;
      setValue(
        key as keyof TPlaqueControlRecordChartFormValues,
        chartData[key as keyof TPlaqueControlRecordChartFormValues]
      );
    });
    watch(PlaqueControlRecordChartFields.round1PlaqueIndex).length === 128 &&
      setRound1All(true);
    watch(PlaqueControlRecordChartFields.round2PlaqueIndex).length === 128 &&
      setRound2All(true);
    setIsLoading(false);
  };

  useEffect(() => {
    if (chartInfoResponse) {
      handleSuccessGetChartInfo(chartInfoResponse);
    }
  }, []);

  // 차트 조회
  useEffect(() => {
    if (!chartInfoResponse) return;
    const chartInfo =
      category === 'practice'
        ? (chartInfoResponse.chartInfo as TPracticeChartInfo)
        : (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    setIsLoading(true);
    setChartInfo(chartInfo);
  }, [activeTabIndex, chartInfoResponse]);

  // 차트 등록
  const onValid = (
    isAutoSave: boolean,
    data: TPlaqueControlRecordChartFormValues
  ) => {
    const formData = new FormData();

    formData.append(
      'request',
      new Blob(
        [
          JSON.stringify({
            chartType,
            chartData: data,
          }),
        ],
        {
          type: 'application/json',
        }
      )
    );
    chartRegister({
      category,
      id: id,
      data: formData,
      isEdit: isEdit,
      isAutoSave,
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
      onLoading: setIsLoading,
      onConfirm: () => {
        openModal({
          element: (
            <ConfirmChartRegister
              category={category}
              chartType={chartType}
              id={id}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              chartName={chartInfoResponse?.chartName}
              data={formData}
            />
          ),
        });
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => onValid(false, data))}>
          <PlaqueControlRecordChart
            category={category}
            round1All={round1All}
            setRound1All={setRound1All}
            round2All={round2All}
            setRound2All={setRound2All}
            isRegister={isRegister}
            isReadOnly={isReadOnly}
            setCanvas1Ref={setCanvas1Ref}
            setCanvas2Ref={setCanvas2Ref}
            activeTabIndex={activeTabIndex}
          />
          {(Number(query.get('tab')) === 0 ||
            Number(query.get('tab')) === 2) && (
            <ChartBottomBtn
              category={category}
              id={id}
              courseId={query.get('courseId') || undefined}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              studentUserId={query.get('studentUserId') || undefined}
              isRegister={isRegister}
              isEdit={isEdit}
              isReadOnly={isReadOnly}
              isChart={true}
              chartName={chartInfoResponse?.chartName}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default PlaqueControlRecordChartRegister;
